/* eslint-disable no-eval */
import {Injectable, Inject} from '@angular/core';
import { DoorActiveConfiguration, WindowActiveConfiguration } from '@icc/window-door/types';
import {APP_CONFIG, AppConfig, AppConfigFactory} from '@icc/shared/config';
import { Common, IssueLevel } from '@icc/shared/helpers';
import { InfoService } from '@icc/shared/helpers';
import { WindowAccessoriesService } from '../accessories/accessories.service';
import { ColorsDefaultsService } from '@icc/construction-elements/configurator';
import { ColorMappingService } from '@icc/construction-elements/configurator';
import { core } from '@icc/shared/helpers';
import { Occurrence } from '@icc/shared/occurrence.interface';
import { ActiveSash } from '@icc/window-door/types';
import { BrowserProfilesService } from '../profiles/profiles.service';
import { ConfiguratorsDataService, EventBusService, TranslateService, BaseIssuesService } from "@icc/shared";

@Injectable()
export class DependenciesService {

    occurrence: Occurrence[] = [];

    constructor(
        private eventBusService: EventBusService,
        @Inject(APP_CONFIG) private config: AppConfigFactory,
        private configuratorsDataService: ConfiguratorsDataService,
        private issuesService: BaseIssuesService,
        private infoService: InfoService,
        private profilesService: BrowserProfilesService,
        private accessoriesService: WindowAccessoriesService,
        private colorsDefaultsService: ColorsDefaultsService,
        private colorMappingService: ColorMappingService,
        private translateService: TranslateService,
    ) {
        if (this.config().IccConfig.Configurators.dependencies) {
            this.eventBusService.subscribe(['processDependencies'], data => {
                if (
                    (<WindowActiveConfiguration>data.activeConfiguration) &&
                    Common.isDefined(
                        (<WindowActiveConfiguration>data.activeConfiguration).Layout
                    ) &&
                    Common.isDefined(
                        (<WindowActiveConfiguration>data.activeConfiguration).Layout?.id
                    ) &&
                    (<WindowActiveConfiguration>data.activeConfiguration).Layout?.id
                ) {
                    this.process(<WindowActiveConfiguration>data.activeConfiguration);
                } else {
                    this.infoService.dismissInfoModal();
                }
            });
        }
    }

    /**
     * Funkcja sprawdzająca zależności
     * @param {object} conf Konfiguracja
     * @memberof DependenciesService
     */
    process(conf) {
        if (Common.isObject(conf)) {
            this.issuesService.unregister('dependencyBlockade', conf);
            conf.Dependencies = [];
            conf.InfoContext = [];
            conf.OfferInfo = [];
            const dependencies = this.configuratorsDataService.data.dependencies || [];
            const sashesDependenciesAccessories = core.copy(conf.Sashes.map(sash => sash.hardware.filter(el => el.accessory.fromDependency && el.accessory.changedColor)));
            conf.Sashes.map(sash => sash.hardware = sash.hardware.filter(el => !el.accessory.fromDependency));
            this.occurrence = conf.DependenciesOccurrence;

            let hasChanges = true;
            let iterationCount = 0;
            const maxIterations = 10;
            let previousConfState = this.createConfigSnapshot(conf);

            while (hasChanges && iterationCount < maxIterations) {
                this.issuesService.unregister('dependencyBlockade', conf);
                conf.Dependencies = [];
                conf.InfoContext = [];
                conf.OfferInfo = [];
                hasChanges = false;

                dependencies.forEach(dependency => {
                    if (dependency.conditions.length && dependency.actions.length) {
                        if (
                            dependency.conditions.some(
                                el => el.blockType === 'sash'
                            )
                            || dependency.actions.some(
                                el => el.blockType === 'sash' || el.valueType === 'sash'
                            )
                        ) {
                            conf.Sashes.forEach((sash, index) => {
                                if (
                                    Common.isObject(sash)
                                ) {
                                    const checkResult = this.check(dependency, dependency.conditions, dependency.id+'-s'+sash.id, conf, sash);
                                    const check = eval(checkResult.check);
                                    const dependencyAssociationIds = checkResult.dependencyAssociationIds;
                                    this.processDependency(dependency, conf, sash, check, dependencyAssociationIds, sashesDependenciesAccessories[index]);
                                }
                            }, this);
                        } else {
                            const checkResult = this.check(dependency, dependency.conditions, dependency.id, conf);
                            const check = eval(checkResult.check);
                            const dependencyAssociationIds = checkResult.dependencyAssociationIds;

                            this.processDependency(dependency, conf, null, check, dependencyAssociationIds, []);
                        }
                    }
                }, this);

                const currentConfState = this.createConfigSnapshot(conf);
                if (previousConfState !== null && !this.areConfigSnapshotsEqual(previousConfState, currentConfState)) {
                    hasChanges = true;
                }
                previousConfState = currentConfState;

                iterationCount++;
            }

            if (iterationCount >= maxIterations) {
                console.warn('DependenciesService',
                    `Osiągnięto maksymalną liczbę iteracji (${maxIterations}) podczas przetwarzania zależności. ` +
                    `Może to oznaczać pętlę nieskończoną lub zbyt skomplikowane zależności kaskadowe.`
                );
            }

            this.eventBusService.post({
                key: 'processedDependencies',
                value: null,
            });
        }
    }

    private processDependency(dependency: any, conf: any, sash: ActiveSash | null, check: any, dependencyAssociationIds: number[], sashesDependenciesAccessories: number[]) {
        if (dependency.actions.find(action => action.type === 'addRemovableAccessoryToConstruction')) {
            this.removeAccessoriesBasedOnOccurrences(dependency, conf, check);
            if (!check) {
                this.removeDependentAccessories(conf, dependency.id);
            }
        }
        const addRemovableAccessoryToConstruction = dependency.actions.find(a => a.type === 'addRemovableAccessoryToConstruction');
        const occurrence = this.occurrence.find(k => k.id === dependency.id && (k.kind === 'removableAccessory' || !k.kind));
        const occurrenceCorrect = !occurrence?.occurrence || occurrence.occurrence <= 1;
        if (check && (occurrenceCorrect || !addRemovableAccessoryToConstruction)) {
            eval(this.check(dependency, dependency.actions, dependency.id+(sash?.id != null ? '-s'+sash.id : ''), conf, sash, sashesDependenciesAccessories, dependencyAssociationIds).check);

            if (dependency.conditions.find(condition => condition.type === 'frameProfile' || condition.type === 'thresholdProfile') &&
                dependency.actions.find(p => p.type === 'blockade' && p.value === 'blockade_to_configuration') &&
                conf.Dependencies.find(d => d.id === dependency.id && d.type === 'blockade_to_configuration')
            ) {
                this.validateFrames(conf);
            }
        }

        if (dependency.actions.find(action => action.type === 'info')) {
            this.setDependencyOccurrence(true, check, dependency, 'info');
        }
    }

    private removeAccessoriesBasedOnOccurrences(dependency: any, _conf: any, check: any) {
        this.setDependencyOccurrence(check, check, dependency, 'removableAccessory');
    }

    private setDependencyOccurrence(valid: boolean, check: boolean, dependency: any, kind: 'removableAccessory' | 'info') {
        const foundDependency = this.occurrence.find(k => k.id === dependency.id && (k.kind === kind || !k.kind && kind === 'removableAccessory'));
        if (valid && check) {
            if (!foundDependency) {
                this.occurrence.push({ id: dependency.id, occurrence: 1, kind });
            } else {
                foundDependency.occurrence += 1;
            }
        } else if (foundDependency) {
            foundDependency.occurrence = 0;
        }
    }

    /**
     * Funkcja sprawdzająca warunki i tworząca wyrażenie
     * @param {array} conditions     warunki
     * @param {number} dependencyId  Id zależności
     * @param {object} [conf={}]     Konfiguracja
     * @param {object} [sash=null]   Skrzydło
     * @returns {string}
     * @memberof DependenciesService
     */
    check(dependency, conditions, dependencyId, conf: WindowActiveConfiguration, sash = null, sashDependenciesAccessories = [], conditionDependencyAssociationIds = []) {
        let check = '';
        const dependencyAssociationIds = [];
        const blockade = dependency.actions.find((p) => p.type === 'addAccessoryToConstruction');
        // eslint-disable-next-line max-statements
        conditions.forEach(condition => {
            if (blockade && condition.for !== 'actions' && condition.value) {
                this.removeDependentItems(condition.value, conf, dependencyId);
                dependencyAssociationIds.push(condition.value);
            } else if (blockade && condition.type === 'hasAlushell') {
                this.removeDependentAccessories(conf, dependencyId);
            }
            switch (condition.type) {
                case 'productType':
                    check += ' ' + this.checkProductType(condition.value, conf); break;
                case 'system':
                    check += ' ' + this.checkSystem(condition.value, conf); break;
                case 'fitting':
                    check += ' ' + this.checkFitting(condition.value, conf); break;
                case 'sashType':
                    check += ' ' + this.checkSashType(condition.value, sash); break;
                case 'sashFillingOuterType':
                    check += ' ' + this.checkSashFillingOuterType(condition.value, conf); break;
                case 'sashFillingInnerType':
                    check += ' ' + this.checkSashFillingInnerType(condition.value, conf); break;
                case 'sashTypeInConstruction':
                    check += ' ' + this.checkSashTypeInConstruction(condition.value, conf); break;
                case 'withoutSashType':
                    check += ' ' + this.checkWithoutSashType(condition.value, conf); break;
                case 'sashWeight':
                    check += ' ' + this.checkSashWeight(condition.value, sash); break;
                case 'shape':
                    check += ' ' + this.checkShape(condition.value, conf); break;
                case 'sashHeight':
                    check += ' ' + this.checkSashHeight(condition.value, sash); break;
                case 'sashWidth':
                    check += ' ' + this.checkSashWidth(condition.value, sash); break;
                case 'height':
                    check += ' ' + this.checkHeight(condition.value, conf); break;
                case 'width':
                    check += ' ' + this.checkWidth(condition.value, conf); break;
                case 'doorHeight':
                    check += ' ' + this.checkDoorHeight(condition.value, conf); break;
                case 'doorWidth':
                    check += ' ' + this.checkDoorWidth(condition.value, conf); break;
                case 'colorType':
                    check += ' ' + this.checkColorType(condition.value, conf); break;
                case 'profile':
                    check += ' ' + this.checkProfile(condition.value, conf); break;
                case 'sizeRange':
                    check += ' ' + this.checkSizeRange(condition.data, conf.Width, conf.Height); break;
                case 'sizeRangeSash':
                    {
                        const sashDimensions = conf.drawData?.sash.find(s => sash && s.sashId == sash.id);
                        check += ' ' + (sashDimensions ? this.checkSizeRange(condition.data, sashDimensions.inner.rect.width, sashDimensions.inner.rect.height) : false); break;
                    }
                case 'lowThreshold':
                    check += ' ' + this.checkLowThreshold(condition.value, conf); break;

                case 'shortening':
                    {
                        const shortening = this.checkShortening(condition.value, conf);
                        check += ' ' + shortening;
                        if (!shortening) {
                            this.removeDependentAccessories(conf, dependencyId);
                        }
                        break;
                    }
                case 'frameShortening':
                    {
                        const frameShortening = this.checkFrameShortening(condition.value, conf);
                        check += ' ' + frameShortening;
                        if (!frameShortening) {
                            this.removeDependentAccessories(conf, dependencyId);
                        }
                        break;
                    }
                case 'accessory':
                    {
                        const checkAccessory = this.checkAccessory(condition.value, conf);
                        this.removeDependentItems(condition.value, conf, dependencyId);

                        const isAccessoryBlockedToConfiguration = dependency.actions.find(p => p.value === 'blockade_to_configuration');
                        if (isAccessoryBlockedToConfiguration) {
                            check += ' ' + true;
                        } else {
                            check += ' ' + checkAccessory;
                        }

                        if (checkAccessory || isAccessoryBlockedToConfiguration) {
                            dependencyAssociationIds.push(condition.value);
                        }
                        break;
                    }
                case 'accessoriesCategory':
                    check += ' ' + this.checkAccessoriesCategory(condition.value, conf); break;
                case 'sashAccessory': {
                    const checkAccessory = this.checkSashAccessory(condition.value, sash);
                    this.removeDependentItems(condition.value, conf, dependencyId);

                    const isAccessoryBlockedToConfiguration = dependency.actions.find(p => p.value === 'blockade_to_configuration');
                    if (isAccessoryBlockedToConfiguration) {
                        check += ' ' + true;
                    } else {
                        check += ' ' + checkAccessory;
                    }
                    if (checkAccessory || isAccessoryBlockedToConfiguration) {
                        dependencyAssociationIds.push(condition.value);
                    }

                    break;
                }
                case 'sashAccessoriesCategory':
                    check += ' ' + this.checkSashAccessoriesCategory(condition.value, sash); break;
                case 'arcHeightInSash':
                    check += ' ' + this.checkArcHeightInSash(condition, conf, sash); break;
                case 'dividerOnSlant':
                    check += ' ' + this.checkDividerOnSlant(conf); break;
                case 'dividerOnSlantVertical':
                    check += ' ' + this.checkDividerOnSlantVertical(conf); break;
                case 'mosquitoType':
                    check += ' ' + this.checkMosquitoType(condition.value, sash); break;
                case 'lock':
                    check += ' ' + this.checkLock(condition.value, conf); break;
                case 'pull':
                    {
                        const checkPull = this.checkPull(condition.value, conf);
                        this.removeDependentItems(condition.value, conf, dependencyId);
                        const isBlockadeAvailable = this.isPullBlockadeAvailable(conf, dependency, condition);

                        if (isBlockadeAvailable) {
                            check += ' ' + true;
                        } else {
                            check += ' ' + checkPull;
                        }

                        if ((isBlockadeAvailable) || checkPull) {
                            dependencyAssociationIds.push(condition.value);
                        }

                        break;
                    }
                case 'extension':
                    {
                        const accessoryDependencies = dependency.conditions.filter(d => d.type === 'accessory');
                        const accessoryIds = accessoryDependencies && accessoryDependencies.map(k => k.value);

                        const isHingeViable = conf.Hinge?.id && accessoryIds.includes(conf.Hinge.id);
                        const blockedToConfiguration = dependency.actions.some((p) => p.value === 'blockade_to_configuration');
                        const isHingeBlockedToConfiguration = isHingeViable && blockedToConfiguration;
                        this.configuratorsDataService.data.dependencies.find(d => d.id === dependency.id).blockedExtension = isHingeBlockedToConfiguration;

                        if (blockedToConfiguration) {
                            check += ' ' + true;
                        } else {
                            check += ' ' + this.checkExtension(condition.value, conf);
                        }

                        break;
                    }
                case 'sashLayoutVariant':
                    {
                        const accessoryDependencies = dependency.conditions.filter(d => d.type === 'accessory');
                        const accessoryIds = accessoryDependencies && accessoryDependencies.map(k => k.value);

                        const isHingeViable = conf.Hinge?.id && accessoryIds.includes(conf.Hinge.id);
                        const blockedToConfiguration = dependency.actions.some((p) => p.value === 'blockade_to_configuration');
                        const isHingeBlockedToConfiguration = isHingeViable && blockedToConfiguration;
                        this.configuratorsDataService.data.dependencies.find(d => d.id === dependency.id).blockedSashLayoutVariant = isHingeBlockedToConfiguration;

                        if (blockedToConfiguration) {
                            check += ' ' + true;
                        } else {
                            check += ' ' + this.checkSashLayoutVariant(condition.value, conf);
                        }

                        break;
                    }
                case 'frameColor': {
                    const accessoryDependencies = dependency.conditions.filter(d => d.type === 'accessory');
                    const accessoryId = accessoryDependencies && accessoryDependencies.map(k => k.value);
                    const innerFrameColor = conf.Colors.frame.inner?.id;
                    const outerFrameColor = conf.Colors.frame.outer?.id;
                    const frameSide = condition.value.frameSides;
                    const colors = condition.value.windowColors.map(Number);
                    const blockedToConfiguration = dependency.actions.some((p) => p.value === 'blockade_to_configuration' || p.type === 'blockadeAccessory');

                    if (frameSide === 'inner' && colors.includes(innerFrameColor) && blockedToConfiguration) {
                        check += ' ' + true;
                        this.getBlockedAccessories(accessoryId, conf);
                    } else if (frameSide === 'outer' && colors.includes(outerFrameColor) && blockedToConfiguration) {
                        check += ' ' + true;
                        this.getBlockedAccessories(accessoryId, conf);
                    } else if (frameSide === 'both' && (colors.includes(outerFrameColor) && colors.includes(innerFrameColor)) && blockedToConfiguration) {
                        check += ' ' + true;
                        this.getBlockedAccessories(accessoryId, conf);
                    } else if (frameSide === 'outer_or_inner' && (colors.includes(outerFrameColor) || colors.includes(innerFrameColor)) && blockedToConfiguration) {
                        check += ' ' + true;
                        this.getBlockedAccessories(accessoryId, conf);
                    } else {
                        check += ' ' + false;
                    }

                    break;
                }
                case 'frameColorGroups': {
                    const accessoryDependencies = dependency.conditions.filter(d => d.type === 'accessory');
                    const accessoryId = accessoryDependencies && accessoryDependencies.map(k => k.value);
                    const innerFrameColorGroups = conf.Colors.frame.inner?.groups?.map(Number) || [];
                    const outerFrameColorGroups = conf.Colors.frame.outer?.groups?.map(Number) || [];
                    const frameSide = condition.value.frameSides;
                    const colorGroups = condition.value.windowColorGroups.map(Number);
                    const blockedToConfiguration = dependency.actions.some((p) => p.value === 'blockade_to_configuration' || p.type === 'blockadeAccessory');

                    const innerFrameColorGroupsInColorGroups = innerFrameColorGroups.some(innerFrameColorGroup => colorGroups.includes(innerFrameColorGroup));
                    const outerFrameColorGroupsInColorGroups = outerFrameColorGroups.some(outerFrameColorGroup => colorGroups.includes(outerFrameColorGroup));

                    if (frameSide === 'inner' && innerFrameColorGroupsInColorGroups && blockedToConfiguration) {
                        check += ' ' + true;
                        this.getBlockedAccessories(accessoryId, conf);
                    } else if (frameSide === 'outer' && outerFrameColorGroupsInColorGroups && blockedToConfiguration) {
                        check += ' ' + true;
                        this.getBlockedAccessories(accessoryId, conf);
                    } else if (frameSide === 'both' && (innerFrameColorGroupsInColorGroups && outerFrameColorGroupsInColorGroups) && blockedToConfiguration) {
                        check += ' ' + true;
                        this.getBlockedAccessories(accessoryId, conf);
                    } else if (frameSide === 'outer_or_inner' && (innerFrameColorGroupsInColorGroups || outerFrameColorGroupsInColorGroups) && blockedToConfiguration) {
                        check += ' ' + true;
                        this.getBlockedAccessories(accessoryId, conf);
                    } else {
                        check += ' ' + false;
                    }

                    break;
                }
                case 'frameProfile': {
                    const blockedToConfiguration = dependency.actions.some((p) => p.value === 'blockade_to_configuration');
                    if (blockedToConfiguration) {
                        check += ' ' + true;
                    } else {
                        check += ' ' + this.checkFrameProfile(condition.value, conf);
                    }
                    break;
                }
                case 'thresholdProfile': {
                    const blockedToConfiguration = dependency.actions.some((p) => p.value === 'blockade_to_configuration');
                    if (blockedToConfiguration) {
                        check += ' ' + true;
                    } else {
                        check += ' ' + this.checkThresholdProfile(condition.value, conf);
                    }
                    break;
                }
                case 'component':
                    check += ' ' + this.checkComponents(condition.value, conf); break;
                case 'addAccessoryToConstruction':
                    check += ' ' + this.addAccessoryToConstruction(condition, conf, dependencyId, conditionDependencyAssociationIds); break;
                case 'addRemovableAccessoryToConstruction':
                    check += ' ' + this.addAccessoryToConstruction(condition, conf, dependencyId, conditionDependencyAssociationIds); break;
                case 'addAccessoryToSash':
                    check += ' ' + this.addAccessoryToSash(condition.value, sash, conf, sashDependenciesAccessories); break;
                case 'openingBracket':
                    check += ' ('; break;
                case 'closingBracket':
                    check += ' )'; break;
                case 'and':
                    check += ' &&'; break;
                case 'or':
                    check += ' ||'; break;
                case 'not':
                    check += ' !'; break;
                case 'info':
                    check += this.info(condition, dependencyId); break;
                case 'blockade':
                    check += this.blockade(condition.value, dependencyId); break;
                case 'surcharge':
                    check += this.surcharge(condition, dependencyId, sash); break;
                case 'infoOffer':
                    check += this.infoOffer(condition); break;
                case 'handleOuter':
                    check += ' ' + this.checkHandleOuter(condition.value, conf); break;
                case 'doorViewerPunching':
                    check += ' ' + this.checkDoorViewerPunching(conf); break;
                case 'infoContext':
                    check += this.infoContext(condition); break;
                case 'blockadeAccessory':
                    check += this.blockade('blockadeAccessory', dependencyId); break;
                case 'blockadeProfile':
                    check += this.blockade('blockadeProfile', dependencyId); break;
                case 'slatCount':
                    check += this.checkSlat(conf); break;
                case 'hasAlushell': {}
                    check += this.checkHasAlushell(conf); break;
                case 'alushellSash':
                    check += this.checkAlushellColorGroup(condition.value, conf, 'sash'); break;
                case 'alushellFrame':
                    check += this.checkAlushellColorGroup(condition.value, conf, 'frame'); break;
            }
        });

        return {check, dependencyAssociationIds};
    }


    checkSashLayoutVariant(dependencyValue: { sashesLayoutsVariantsOptions, sashesLayoutsVariantsTypes}, conf: WindowActiveConfiguration) {
        const ALL_SASHES_LAYOUTS_VARIANTS = "ALL_SASHES_LAYOUTS_VARIANTS";
        const layoutsIds = dependencyValue?.sashesLayoutsVariantsTypes?.map(k => k.id);

        if (!layoutsIds.length) {
            return;
        }

        const selectedAllSashesVariants = layoutsIds.includes(ALL_SASHES_LAYOUTS_VARIANTS);
        const isCurrentLayoutBlocked = dependencyValue.sashesLayoutsVariantsTypes.some((k) => k.id === conf.Layout.id);

        if (dependencyValue?.sashesLayoutsVariantsOptions?.includes('from_hinge_side')) {
            const handlePosition = conf.Sashes?.find((k) => k.type?.handle_position)?.type?.handle_position;

            switch(handlePosition) {
                case 'L': {
                    return selectedAllSashesVariants && conf.OwnedSashesTypes.doorRightLight ||
                    isCurrentLayoutBlocked
                }
                case 'R': {
                    return selectedAllSashesVariants && conf.OwnedSashesTypes.doorLeftLight ||
                    isCurrentLayoutBlocked
                }
                default: return false;
            }
        } else {
            return (
                isCurrentLayoutBlocked ||
                (selectedAllSashesVariants ? (
                    conf.OwnedSashesTypes.doorRightLight ||
                    conf.OwnedSashesTypes.doorLeftLight ||
                    conf.OwnedSashesTypes.doorTopLight) : false)
            )
        }
    }

    checkExtension(value: { extensionOptions, extensionTypes }, conf: WindowActiveConfiguration) {
        const ALL_EXTENSIONS = "ALL_EXTENSIONS";
        const extensionIds = value.extensionTypes.map(k => k.id);

        if (!extensionIds.length) {
            return;
        }

        if (value?.extensionOptions?.includes('from_hinge_side')) {
            const handlePosition = conf.Sashes.find((k) => k.type.handle_position).type.handle_position;
            switch(handlePosition) {
                case 'L': {
                    const sideProfiles = conf.SideProfiles.filter(k => k.side === 'right');
                    return (
                        (extensionIds.includes(ALL_EXTENSIONS) && conf.SideProfiles.length) ||
                        sideProfiles.some((sp) => extensionIds.includes(sp.profileId))
                    );
                }
                case 'R': {
                    const sideProfiles = conf.SideProfiles.filter(k => k.side === 'left');
                    return (
                        (extensionIds.includes(ALL_EXTENSIONS) && conf.SideProfiles.length) ||
                        sideProfiles.some((sp) => extensionIds.includes(sp.profileId))
                    );
                }
                default: return false
            }
        } else {
            return (
                value.extensionTypes.some((k) => conf.SideProfiles.some(sp => sp.profileId === k.id)) ||
                extensionIds.includes(ALL_EXTENSIONS) && conf.SideProfiles.length
            );
        }
    }
    /**
     * Wyświetlanie informacji
     * @param {object} info Informacja
     * @returns {string|bolean}
     * @memberof DependenciesService
     */
    info(info, dependencyId) {
        const occurrence = this.occurrence.find(k => String(k.id).split('-')[0] === String(dependencyId).split('-')[0] && k.kind === 'info');
        const hasNotOccurred = !occurrence?.occurrence || occurrence.occurrence < 1;
        if (hasNotOccurred && ['info', 'warning', 'error'].includes(info.valueType)) {
            const escapedString = info.value.replace(/`/g, '\\`').replace(/\$/g, '\\$');
            return ' (this.infoService.openWarning(`' + escapedString + '`))';
        }
        return true;
    }

    /**
     * Wyświetlanie informacji kontekstowej
     * @param {object} info Informacja
     * @returns {string|bolean}
     * @memberof DependenciesService
     */
    infoContext(info) {
        return ' conf.InfoContext.push({valueType: "' + info.valueType + '", value: "' + info.value + '"})';

    }

    /**
     * Wyświetlanie informacji na ofercie
     * @param {object} info Informacja
     * @returns {string|bolean}
     * @memberof DependenciesService
     */
    infoOffer(info) {
        const escapedString = info.value.replace(/`/g, '\\`').replace(/\$/g, '\\$');
        return ' (conf.OfferInfo.push(`' + escapedString + '`))';
    }

    /**
     * Sprawdzanie klamki zewnętrznej
     * @param {string} value Wartość do sprawdzenia
     * @param {object} conf  Konfiguracja pozycji
     * @returns {boolean}
     * @memberof DependenciesService
     */
    checkHandleOuter(value, conf) {
        if (conf.HandleType !== 'InnerLever' &&  Common.isObject(conf.HandleOuter) && Common.isDefined(conf.HandleOuter.id) && Number(value) === Number(conf.HandleOuter.id)) {
            return true;
        }
        return false;
    }

    /**
     * Sprawdzanie czy jest otworowanie pod wizjer
     * @param {object} conf  Konfiguracja pozycji
     * @returns {boolean}
     * @memberof DependenciesService
     */
    checkDoorViewerPunching(conf) {
        if (conf.type === 'door' && conf?.doorHardware?.doorViewerPunching) {
            return true;
        }
        return false;
    }

    /**
     * Blokowanie dodawania do oferty lub wymuszenie przekazania do wyceny
     * @param {object} blockade     Blokada
     * @param {number} dependencyId Id zależności
     * @returns {string|bolean}
     * @memberof DependenciesService
     */
    blockade(blockade, dependencyId) {
        if (blockade == 'blockade_to_configuration') {
            return ' conf.Dependencies.push({id: "' + dependencyId + '", type: "blockade_to_configuration"})';
        } else if (blockade == 'blockade') {
            return ' (this.issuesService.simpleRegister("dependencyBlockade", "Dependency Blockade", this.translateService.instant("CONFIGURATOR|Nie można zapisać obecnej konfiguracji"), conf, {blockAddToOffer: true, showMessage: false, logLevel: ' + IssueLevel.NONE + '}))';
        } else if (blockade == 'valuation') {
            return ' conf.Dependencies.push({id: "' + dependencyId + '", type: "valuation"})';
        } else if (blockade === 'blockadeAccessory') {
            return ' conf.Dependencies.push({id: "' + dependencyId + '", type: "blockadeAccessory"})';
        } else if (blockade === 'blockadeProfile') {
            return ' conf.Dependencies.push({id: "' + dependencyId + '", type: "blockadeProfile"})';
        }
        return true;
    }

    /**
     * Dopłata do skrzydła lub konstrukcji
     * @param {object} surcharge    Dopłata
     * @param {number} dependencyId Id zależnośći
     * @param {object} sash         Skrzydło
     * @returns {string|bolean}
     * @memberof DependenciesService
     */
    surcharge(surcharge, dependencyId, sash) {
        if (surcharge.valueType == 'sash') {
            return ' conf.Dependencies.push({id: "' + dependencyId + '", type: "sash", sashId: ' + sash.id + ', value: ' + surcharge.value + '})';
        } else if (surcharge.valueType == 'construction') {
            return ' conf.Dependencies.push({id: "' + dependencyId + '", type: "construction", sashId: null , value: ' + surcharge.value + '})';
        }
        return true;
    }

    /**
     * Sprawdzanie typu produktu
     * @param {string} value Wartość do sprawdzenia
     * @param {object} conf  Konfiguracja pozycji
     * @returns {boolean}
     * @memberof DependenciesService
     */
    checkProductType(value, conf) {
        if (Common.isObject(conf.System) && Common.isDefined(conf.System.type) && value == conf.System.type) {
            return true;
        }
        return false;
    }

    /**
    * Sprawdzanie systemu produktu
    * @param {string} value Wartość do sprawdzenia
    * @param {object} conf  Konfiguracja pozycji
    * @returns {boolean}
    * @memberof DependenciesService
    */
    checkSystem(value, conf) {
        if (Common.isObject(conf.System) && Common.isDefined(conf.System.id) && value && value.map(Number).indexOf(Number(conf.System.id)) > -1) {
            return true;
        }
        return false;
    }

    /**
     * Sprawdzanie okucia
     * @param {string} value Wartość do sprawdzenia
     * @param {object} conf  Konfiguracja pozycji
     * @returns {boolean}
     * @memberof DependenciesService
     */
    checkFitting(value, conf) {
        if (Common.isObject(conf.Fitting) && Common.isDefined(conf.Fitting.id) && value == conf.Fitting.id) {
            return true;
        }
        return false;
    }

    /**
    * Sprawdzanie typu skrzydła
    * @param {string} value Wartość do sprawdzenia
    * @param {object} sash  Konfiguracja skrzydła
    * @returns {boolean}
    * @memberof DependenciesService
    */
    checkSashType(value, sash) {
        if (sash != null && Common.isObject(sash.type) && Common.isDefined(sash.type.id) && (value == sash.type.id || Array.isArray(value) && value.map(Number).indexOf(Number(sash.type.id)) > -1)) {
            return true;
        }
        return false;
    }

    /**
     * Sprawdzanie wzoru wewnętrznego
     * @param value Wartość (identyfikator) do sprawdzenia
     * @param sash Konfiguracja skrzydła
     * @returns
     * @memberof DependenciesService
     */
    checkSashFillingInnerType(value, conf) {
        if (value && conf.Sashes) {
            return conf.Sashes.find(sash => value.map(Number).indexOf(Number(sash?.panelInner?.id)) > -1) ? true : false;
        }
    }

    /**
     * Sprawdzanie wzoru zewnętrznego
     * @param value Wartość (identyfikator) do sprawdzenia
     * @param sash Konfiguracja skrzydła
     * @returns
     * @memberof DependenciesService
     */
    checkSashFillingOuterType(value, conf) {
        if (value && conf.Sashes) {
            return conf.Sashes.find(sash => value.map(Number).indexOf(Number(sash?.glazing?.id)) > -1) ? true : false;
        }
    }

    /**
     * Sprawdzanie typu skrzydła w całej konstrukcji
     * @param {string} value Wartość do sprawdzenia
     * @param {object} conf  Konfiguracja pozycji
     * @returns {boolean}
     * @memberof DependenciesService
     */
    checkSashTypeInConstruction(value, conf) {
        if (
            conf != null
            && Common.isArray(conf.Sashes)
            && conf.Sashes.some(
                sash =>
                    Common.isObject(sash.type)
                    && Common.isDefined(sash.type.id)
                    && (value == sash.type.id ||
                        Array.isArray(value) && value.map(Number).indexOf(Number(sash.type.id)) > -1)
                )
        ) {
            return true;
        }
        return false;
    }
    /**
     * Sprawdzanie nieobecności typu skrzydła
     * @param {string} value Wartość do sprawdzenia
     * @param {object} sash  Konfiguracja skrzydła
     * @returns {boolean}
     * @memberof DependenciesService
     */
    checkWithoutSashType(value, conf) {
        if (
            conf != null
            && Common.isArray(conf.Sashes)
            && conf.Sashes.every(sash => Common.isObject(sash.type) && Common.isDefined(sash.type.id) && (Array.isArray(value) ? value.map(Number).indexOf(Number(sash.type.id)) > -1 : value != sash.type.id))
        ) {
            return true;
        }
        return false;
    }

    /**
     * Sprawdzanie wagi skrzydła
     * @param {string} value Wartość do sprawdzenia
     * @param {object} sash  Konfiguracja skrzydła
     * @returns {boolean}
     * @memberof DependenciesService
     */
    checkSashWeight(value, sash) {
        if (sash != null && Common.isDefined(sash.weight) && sash.weight != null && sash.weight !== false && value.from <= sash.weight && value.to >= sash.weight) {
            return true;
        }
        return false;
    }

    /**
     * Sprawdzanie kształtu
     * @param {string} value Wartość do sprawdzenia
     * @param {object} conf  Konfiguracja pozycji
     * @returns {boolean}
     * @memberof DependenciesService
     */
    checkShape(value, conf) {
        if (Common.isObject(conf.Shape) && Common.isDefined(conf.Shape.shape) && value == conf.Shape.shape) {
            return true;
        }
        return false;
    }

    /**
     * Sprawdzanie wysokości skrzydła
     * @param {string} value Wartość do sprawdzenia
     * @param {object} sash  Konfiguracja skrzydła
     * @returns {boolean}
     * @memberof DependenciesService
     */
    checkSashHeight(value, sash) {
        if (sash != null && Common.isDefined(sash.rHeight) && sash.rHeight != null && sash.rHeight !== false && value.from <= sash.rHeight && value.to >= sash.rHeight) {
            return true;
        }
        return false;
    }

    /**
     * Sprawdzanie szerokości skrzydła
     * @param {string} value Wartość do sprawdzenia
     * @param {object} sash  Konfiguracja skrzydła
     * @returns {boolean}
     * @memberof DependenciesService
     */
    checkSashWidth(value, sash) {
        if (sash != null && Common.isDefined(sash.rWidth) && sash.rWidth != null && sash.rWidth !== false && value.from <= sash.rWidth && value.to >= sash.rWidth) {
            return true;
        }
        return false;
    }

    /**
     * Sprawdzanie wysokości konstrukcji
     * @param {string} value Wartość do sprawdzenia
     * @param {object} conf  Konfiguracja pozycji
     * @returns {boolean}
     * @memberof DependenciesService
     */
    checkHeight(value, conf) {
        if (Common.isDefined(conf.Height) && conf.Height != null && value.from <= conf.Height && value.to >= conf.Height) {
            return true;
        }
        return false;
    }

    /**
     * Sprawdzanie szerokości konstrukcji
     * @param {string} value Wartość do sprawdzenia
     * @param {object} conf  Konfiguracja pozycji
     * @returns {boolean}
     * @memberof DependenciesService
     */
    checkWidth(value, conf) {
        if (Common.isDefined(conf.Width) && conf.Width != null && value.from <= conf.Width && value.to >= conf.Width) {
            return true;
        }
        return false;
    }

    checkDoorHeight(value, conf) {
        const doorHeight = (Number(conf?.Height) || 0) - (Number(conf?.doorSizes?.topLightSize) || 0)- (Number(conf?.doorSizes?.bottomLightSize) || 0);

        if (value.from <= doorHeight && value.to >= doorHeight) {
            return true;
        }

        return false;
    }

    checkDoorWidth(value, conf) {
        const doorWidth = (Number(conf?.Width) || 0) - (Number(conf?.doorSizes?.doorLeftLight) || 0)- (Number(conf?.doorSizes?.doorRightLight) || 0);
        return value.from <= doorWidth && value.to >= doorWidth;
    }
    /**
     * Sprawdzanie typu koloru
     * @param {string} value Wartość do sprawdzenia
     * @param {object} conf  Konfiguracja pozycji
     * @returns {boolean}
     * @memberof DependenciesService
     */
    checkColorType(value, conf: WindowActiveConfiguration) {
        if (Common.isDefined(conf.Colors) && conf.Colors.frame) {
            const colors = conf.Colors;
            switch (value) {
                case 'White':
                    return colors.frame.outer?.type === 'white'
                        && colors.frame.inner?.type === 'white';
                case 'Cream':
                    return colors.frame.outer?.type === 'cream'
                        && colors.frame.inner?.type === 'cream';
                case 'Outer':
                    return colors.frame.outer?.id && colors.frame.inner?.isCore;
                case 'Inner':
                    return colors.frame.outer?.isCore && colors.frame.inner?.id;
                case 'Bilateral':
                    return colors.frame.outer?.id
                        && colors.frame.inner?.id
                        && colors.frame.outer?.id === colors.frame.inner?.id;
                case 'Bicolor':
                    return colors.frame.outer?.id
                        && colors.frame.inner?.id
                        && colors.frame.outer?.id !== colors.frame.inner?.id;
            }
        }
        return false;
    }

    /**
     * Sprawdzanie zakresu wymiarów
     * @param {string} value   Wartość do sprawdzenia
     * @param {number} width   Szerokość
     * @param {number} height  Wysokość
     * @returns {boolean}
     * @memberof DependenciesService
     */
    checkSizeRange(poly, width, height) {
        let inside = false;
        if (Common.isDefined(width) && Common.isDefined(height) && width != null && width !== false && height != null && height !== false) {
            const pointX = width;
            const pointY = height;
            for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
                const xi = Number(poly[i].width);
                const yi = Number(poly[i].height);
                const xj = Number(poly[j].width);
                const yj = Number(poly[j].height);
                if (((yi > pointY) != (yj > pointY)) && (pointX < (xj - xi) * (pointY - yi) / (yj - yi) + xi)) {
                    inside = !inside;
                }
            }
        }
        return inside;
    }

    /**
     * Sprawdzanie akcesorium
     * @param {string} value Wartość do sprawdzenia
     * @param {object} conf  Konfiguracja pozycji
     * @returns {boolean}
     * @memberof DependenciesService
     */
    checkAccessory(value, conf) {
        const plates = Number(conf.doorHardware?.plates?.mainLock) === Number(value);
        const outerPlates = Number(conf.doorHardware?.outerPlates?.mainLock) === Number(value);
        const additionalLock = Number(conf.doorHardware?.plates?.additionalLock) === Number(value);
        const outerAdditionalLock = Number(conf.doorHardware?.outerPlates?.additionalLock) === Number(value);
        const lock = Number(conf.Lock?.id) === Number(value);
        const cylinder = Number(conf.doorHardware?.cylinders?.mainLock) === Number(value);
        const cylinderAdditionalLock = Number(conf.doorHardware?.cylinders?.additionalLock) === Number(value);
        const electronicLock = Number(conf.doorHardware?.electronicLock?.id) === Number(value);
        const pull = Number(conf.doorHardware?.pull) === Number(value);
        const hinge = Number(conf.Hinge?.id) === Number(value);
        const handle = Common.isObject(conf.Handle)
            && Common.isDefined(conf.Handle.id)
            && Number(conf.Handle.id) === Number(value);
        const doorViewer = Number(conf.doorHardware?.doorViewer) === Number(value);
        const accessories = Common.isArray(conf.Accessories)
            && conf.Accessories.some(el => Common.isObject(el.accessory)
            && Common.isDefined(el.accessory.id)
            && Number(el.accessory.id) === Number(value));
        const sashAccessory = Common.isArray(conf.Sashes)
            && conf.Sashes.some(sash =>
                (Common.isObject(sash.handle)
                    && Common.isDefined(sash.handle.id)
                    && Number(sash.handle.id) === Number(value)
                )
                || (Common.isArray(sash.hardware)
                    && sash.hardware.some(el => Common.isObject(el.accessory)
                        && Common.isDefined(el.accessory.id)
                        && Number(el.accessory.id) === Number(value)
                    )
                )
            );
        const sideAccessories = Common.isObject(conf.SideAccessories)
            && Object.keys(conf.SideAccessories).some(side => Common.isArray(conf.SideAccessories[side])
                && conf.SideAccessories[side].some(el => Common.isObject(el.accessory)
                    && Common.isDefined(el.accessory.id)
                    && Number(el.accessory.id) === Number(value)
                )
            );

        return plates || outerPlates || additionalLock || outerAdditionalLock || lock || cylinder || cylinderAdditionalLock || electronicLock || pull || hinge || handle || doorViewer || accessories || sashAccessory || sideAccessories;
    }

    /**
     * Sprawdzanie akcesorium w konstrukcji
     * @param {string} value Wartość do sprawdzenia
     * @param {object} conf  Konfiguracja pozycji
     * @returns {boolean}
     * @memberof DependenciesService
     */
    checkConstructionAccessory(value, conf) {
        if (
            Common.isArray(conf.Accessories)
            && conf.Accessories.some(el => Common.isObject(el.accessory)
                && Common.isDefined(el.accessory.id)
                && el.accessory.id == value)
        ) {
            return true;
        }
        return false;
    }

    /**
     * Sprawdzanie akcesorium w skrzydle
     * @param {string} value Wartość do sprawdzenia
     * @param {object} sash  Konfiguracja skrzydła
     * @returns {boolean}
     * @memberof DependenciesService
     */
    checkSashAccessory(value, sash) {
        if ((sash != null
                && ((Common.isObject(sash.handle) && Common.isDefined(sash.handle.id) && sash.handle.id == value)
                    || (Common.isArray(sash.hardware)
                        && sash.hardware.some(el => Common.isObject(el.accessory)
                            && Common.isDefined(el.accessory.id)
                            && el.accessory.id == value))))
        ) {
            return true;
        }
        return false;
    }

    /**
     * Sprawdzanie kategorii akcesoriów
     * @param {string} value Wartość do sprawdzenia
     * @param {object} conf  Konfiguracja pozycji
     * @returns {boolean}
     * @memberof DependenciesService
     */
    checkAccessoriesCategory(value, conf) {
        if ((Common.isObject(conf.Handle)
                && Common.isDefined(conf.Handle.window_accessories_category_id)
                && conf.Handle.window_accessories_category_id == value)
            || (Common.isArray(conf.Accessories)
                && conf.Accessories.some(el => Common.isObject(el.accessory)
                    && Common.isDefined(el.accessory.window_accessories_category_id)
                    && el.accessory.window_accessories_category_id == value))
            || (Common.isArray(conf.Sashes)
                && conf.Sashes.some(sash =>
                    (Common.isObject(sash.handle)
                        && Common.isDefined(sash.handle.window_accessories_category_id)
                        && sash.handle.window_accessories_category_id == value)
                    || (Common.isArray(sash.hardware)
                        && sash.hardware.some(el => Common.isObject(el.accessory)
                            && Common.isDefined(el.accessory.window_accessories_category_id)
                            && el.accessory.window_accessories_category_id == value))))
            || (Common.isObject(conf.SideAccessories)
                && Object.keys(conf.SideAccessories).some(side => Common.isArray(conf.SideAccessories[side])
                    && conf.SideAccessories[side].some(el => Common.isObject(el.accessory)
                        && Common.isDefined(el.accessory.window_accessories_category_id)
                        && el.accessory.window_accessories_category_id == value)))
        ) {
            return true;
        }
        return false;
    }

    /**
     * Sprawdzanie kategorii akcesoriów w skrzydle
     * @param {string} value Wartość do sprawdzenia
     * @param {object} sash  Konfiguracja skrzydla
     * @returns {boolean}
     * @memberof DependenciesService
     */
    checkSashAccessoriesCategory(value, sash) {
        if ((sash != null
                && ((Common.isObject(sash.handle) && Common.isDefined(sash.handle.window_accessories_category_id) && sash.handle.window_accessories_category_id == value)
                    || (Common.isArray(sash.hardware)
                        && sash.hardware.some(el => Common.isObject(el.accessory)
                            && Common.isDefined(el.accessory.window_accessories_category_id)
                            && el.accessory.window_accessories_category_id == value))))
        ) {
            return true;
        }
        return false;
    }

    /**
     * Sprawdzanie wysokości łuku w skrzydle
     * @param {object} condition Wartość do sprawdzenia
     * @param {object} conf  Konfiguracja pozycji
     * @returns {boolean}
     * @memberof DependenciesService
     */
    checkArcHeightInSash(condition, conf, sash) {
        const openingSide = sash.type.symbol.indexOf('(R)') > -1 ? 'R' : (sash.type.symbol.indexOf('(L)') > -1 ? 'L' : false);
        if (openingSide && conf.drawData?.sashFrame) {
            const sashPoly = conf.drawData.sashFrame.find(o => o.sashId === sash.id);
            const sashHeight = sashPoly.outer.rect.height;

            if (conf.Shape.shape == 'arc'
                && ((conf.Shape.type == 'R' && openingSide == 'R') || (conf.Shape.type == 'L' && openingSide == 'L') || conf.Shape.type == 'F')
                && ((condition.valueType == '>' && (this.getArcHeight(sashPoly, openingSide) / sashHeight * 100) > (condition.value))
                    || (condition.valueType == '<' && (this.getArcHeight(sashPoly, openingSide) / sashHeight * 100) < (condition.value)))
            ) {
                return true;
            }
        }

        return false;
    }

    /**
     * Wyliczanie wysokości łuku w skrzydle
     * @param {object} sashPoly     Wymiary skrzydła
     * @param {string} openingSide  Strona otwierania
     * @returns {number}
     * @memberof DependenciesService
     */
    getArcHeight(sashPoly, openingSide) {
        let arcHeight = 0;
        const sashHeight = sashPoly.outer.rect.height;
        let sidePolyHeight = 0;
        let sidePoly;
        if (openingSide == 'R') {
            sidePoly = sashPoly.sides.find(el => el.outerEdge.side == 'right' && !el.outerEdge.circle);
        } else if (openingSide == 'L') {
            sidePoly = sashPoly.sides.find(el => el.outerEdge.side == 'left' && !el.outerEdge.circle);
        }
        if (sidePoly) {
            sidePolyHeight = sidePoly.rect.height;
        }
        arcHeight = sashHeight - sidePolyHeight;

        return arcHeight;
    }

    /**
     * Sprawdzanie czy słupek jest na połączeniu skosu z poziomą częścią ramy
     * @param {object} conf  Konfiguracja pozycji
     * @returns {boolean}
     * @memberof DependenciesService
     */
    checkDividerOnSlant(conf) {
        const verticalMullions = conf.Mullions.filter(el => el.direction === 'vertical');
         if (conf.Shape.shape === 'poligon' && verticalMullions.length) {
            return verticalMullions.some(mullion => {
                let profile = this.profilesService.getProfile(mullion.profileId);
                let alignment = conf.Alignments.find(el => el.perpendicularMullions && el.perpendicularMullions.bottom && el.perpendicularMullions.bottom.some(elem => elem == mullion.id));
                if (alignment) {
                    const alignmentRectData = conf.drawData.alignment.find(o => o.alignmentId === alignment.id);
                    if (!alignmentRectData?.rect) {
                        return false;
                    }
                    let alignmentRect = alignmentRectData.rect;
                    if ((alignmentRect.x >= (mullion.rx - profile.width / 2) && alignmentRect.x <= (mullion.rx + profile.width / 2))
                        || ((alignmentRect.x + alignmentRect.width) >= (mullion.rx - profile.width / 2) && (alignmentRect.x + alignmentRect.width) <= (mullion.rx + profile.width / 2))
                    ) {
                        return true;
                    }
                } else {
                    if (mullion.ry == 0) {
                        const framePoly = conf.drawData.frame[0].inner.poly;
                        return framePoly.some(point => point.x >= (mullion.rx - profile.width / 2) && point.x <= (mullion.rx + profile.width / 2));
                    } else {
                        let topMullion = conf.Mullions.find(el => el.direction == 'horizontal' && el.multiAlignBottomDiv.length && el.multiAlignBottomDiv.some(div => div.id == mullion.id));
                        let topMullionRect = conf.drawData.mullion.find(o => o.mullionId === topMullion.id).rect;
                        if ((topMullionRect.x >= (mullion.rx - profile.width / 2) && topMullionRect.x <= (mullion.rx + profile.width / 2))
                            || ((topMullionRect.x + topMullionRect.width) >= (mullion.rx - profile.width / 2) && (topMullionRect.x + topMullionRect.width) <= (mullion.rx + profile.width / 2))
                        ) {
                            return true;
                        }
                    }
                    return false;
                }
            });
        }
        return false;
    }

    /**
      * Sprawdzanie czy słupek jest na połączeniu skosu z pionową częścią ramy
      * @param {object} conf  Konfiguracja pozycji
      * @returns {boolean}
      * @memberof DependenciesService
      */
      checkDividerOnSlantVertical(conf) {
        const horizontalMullions = conf.Mullions.filter(el => el.direction === 'horizontal');
        if ((conf.Shape.shape === 'poligon' || conf.Shape.shape === 'arc') && horizontalMullions.length) {
            return horizontalMullions.some(mullion => {
                let profile = this.profilesService.getProfile(mullion.profileId);

                // lewa strona
                let leftAlignment = conf.Alignments.find(el => el.perpendicularMullions && el.perpendicularMullions.right && el.perpendicularMullions.right.some(elem => elem == mullion.id));
                if (leftAlignment) {
                    const alignmentRectData = conf.drawData.alignment.find(o => o.alignmentId === leftAlignment.id);
                    if (!alignmentRectData?.rect) {
                        return false;
                    }
                    let alignmentRect = alignmentRectData.rect;
                    if ((alignmentRect.y >= (mullion.ry - profile.width / 2) && alignmentRect.y <= (mullion.ry + profile.width / 2))) {
                        return true;
                    }
                } else {
                    let leftMullion = conf.Mullions.find(el => el.direction == 'vertical' && el.multiAlignRightDiv.length && el.multiAlignRightDiv.some(div => div.id == mullion.id));
                    if (leftMullion) {
                        let leftMullionRect = conf.drawData.mullion.find(o => o.mullionId === leftMullion.id).rect;
                        if ((leftMullionRect.y >= (mullion.ry - profile.width / 2) && leftMullionRect.y <= (mullion.ry + profile.width / 2))) {
                            return true;
                        }
                    }
                }

                // prawa strona
                let rightAlignment = conf.Alignments.find(el => el.perpendicularMullions && el.perpendicularMullions.left && el.perpendicularMullions.left.some(elem => elem == mullion.id));
                if (rightAlignment) {
                    const alignmentRectData = conf.drawData.alignment.find(o => o.alignmentId === rightAlignment.id);
                    if (!alignmentRectData?.rect) {
                        return false;
                    }
                    let alignmentRect = alignmentRectData.rect;
                    if ((alignmentRect.y >= (mullion.ry - profile.width / 2) && alignmentRect.y <= (mullion.ry + profile.width / 2))) {
                        return true;
                    }
                } else {
                    let rightMullion = conf.Mullions.find(el => el.direction == 'vertical' && el.multiAlignLeftDiv.length && el.multiAlignLeftDiv.some(div => div.id == mullion.id));
                    if (rightMullion) {
                        let rightMullionRect = conf.drawData.mullion.find(o => o.mullionId === rightMullion.id).rect;
                        if ((rightMullionRect.y >= (mullion.ry - profile.width / 2) && rightMullionRect.y <= (mullion.ry + profile.width / 2))) {
                            return true;
                        }
                    }
                }

                const framePoly = conf.drawData.frame[0].inner.poly;
                const mullionDimensions = conf.drawData.mullion.find(el => el.mullionId == mullion.id);
                return framePoly.some(point =>
                    point.y >= (mullion.ry - profile.width / 2) && point.y <= (mullion.ry + profile.width / 2)
                    && (Math.round(point.x) === Math.round(mullionDimensions.rect.x)
                        || Math.round(point.x) === Math.round(mullionDimensions.rect.x + mullionDimensions.rect.width))
                );

            });
        }
        return false;
    }

    /**
     * Sprawdzanie typu siatki w skrzydle
     * @param {string} value Wartość do sprawdzenia
     * @param {object} sash  Konfiguracja skrzydła
     * @returns {boolean}
     * @memberof DependenciesService
     */
    checkMosquitoType(value, sash) {
        if (
            sash != null
            && Common.isObject(sash.mosquito)
            && Common.isDefined(sash.mosquito.Type)
            && Common.isDefined(sash.mosquito.Type.id)
            && sash.mosquito.Type.id == value
        ) {
            return true;
        }
        return false;
    }

    /**
     * Sprawdzanie wybranego zamka
     * @param {string} value Wartość do sprawdzenia
     * @param {object} conf  Konfiguracja konstrukcji
     * @returns {boolean}
     * @memberof DependenciesService
     */
    checkLock(value, conf) {
        if (
            Common.isObject(conf.Lock)
            && Common.isDefined(conf.Lock.id)
            && conf.Lock.id == value
        ) {
            return true;
        }
        return false;
    }

    /**
     * Sprawdzanie wybranego pochwytu
     * @param {string} value Wartość do sprawdzenia
     * @param {object} conf  Konfiguracja konstrukcji
     * @returns {boolean}
     * @memberof DependenciesService
     */
    checkPull(value, conf) {
        if (
            Common.isObject(conf.doorHardware)
            && Common.isDefined(conf.doorHardware.pull)
            && conf.doorHardware.pull == value
        ) {
            return true;
        }
        if (
            Common.isObject(conf.doorHardware)
            && Common.isDefined(conf.doorHardware.recessedPull)
            && conf.doorHardware.recessedPull == value
        ) {
            return true;
        }

        return false;
    }

    /**
     * Sprawdzanie wielu komponentów (wystarczy że jeden jest zaznaczony)
     * @param {string[]} componentPaths Ścieżki do komponentów
     * @param {object} conf  Konfiguracja pozycji
     * @returns {boolean}
     * @memberof DependenciesService
     */
    checkComponents(componentPaths: string[], conf): boolean {
        if (!Array.isArray(componentPaths) || !componentPaths.length) {
            return false;
        }
        return componentPaths.some(path => this.checkComponent(path, conf));
    }

    /**
     * Sprawdzanie stanu komponentu konfiguracji drzwiowej
     * @param {string} componentPath Ścieżka do komponentu (np. 'fullDoor', 'sideLight.left')
     * @param {object} conf  Konfiguracja pozycji
     * @returns {boolean}
     * @memberof DependenciesService
     */
    checkComponent(componentPath: string, conf): boolean {
        if (!DoorActiveConfiguration.is(conf) || !conf.Components) {
            return false;
        }
        if (componentPath !== 'fullDoor' && conf.Components.fullDoor === true) {
            return false;
        }
        if (!this.isComponentAvailableInConf(componentPath, conf)) {
            return false;
        }
        const parts = componentPath.split('.');
        let value: any = conf.Components;
        for (const part of parts) {
            if (value == null || typeof value !== 'object') {
                return false;
            }
            value = value[part];
        }
        return value === true;
    }

    /**
     * Sprawdzanie czy komponent jest dostępny w konfiguracji (analogicznie do ComponentsService)
     * @param {string} componentPath Ścieżka do komponentu
     * @param {object} conf  Konfiguracja pozycji
     * @returns {boolean}
     * @memberof DependenciesService
     */
    private isComponentAvailableInConf(componentPath: string, conf): boolean {
        switch (componentPath) {
            case 'fullDoor':
            case 'activeSash':
            case 'frame':
            case 'threshold':
            case 'hardware':
                return true;
            case 'passiveSash':
                return conf.Sashes?.some(sash => ['DRP', 'DOP'].indexOf(sash.type?.type) > -1) ?? false;
            case 'sideLight.left':
                return Boolean(conf.OwnedSashesTypes?.doorLeftLight);
            case 'sideLight.right':
                return Boolean(conf.OwnedSashesTypes?.doorRightLight);
            case 'sideLight.top':
                return Boolean(conf.OwnedSashesTypes?.doorTopLight);
            case 'extension.left':
                return conf.SideProfiles?.some(p => p.side === 'left') ?? false;
            case 'extension.right':
                return conf.SideProfiles?.some(p => p.side === 'right') ?? false;
            case 'extension.top':
                return conf.SideProfiles?.some(p => p.side === 'top') ?? false;
            case 'bottomExtension':
                return conf.SideProfiles?.some(p => p.side === 'bottom') ?? false;
            case 'portal':
                return Boolean(conf.doorPortal);
            case 'casing':
                return Boolean(conf.Casing?.id);
            case 'accessories':
                return Common.isArray(conf.Accessories) && conf.Accessories.length > 0;
            case 'pull':
                return Boolean(conf.doorHardware?.pull);
            case 'recessedPull':
                return Boolean(conf.doorHardware?.recessedPull);
            default:
                return true;
        }
    }

    /**
     * Dodanie dodatku do całej konstrukcji
     * @param {string} value Wartość do sprawdzenia
     * @param {object} sash  Konfiguracja skrzydła
     * @returns {boolean}
     * @memberof DependenciesService
     */
    // eslint-disable-next-line max-statements
    addAccessoryToConstruction(condition, conf, dependencyId, dependencyAssociationIds?: number[]) {
        const accessory = this.accessoriesService.getAccessory(condition.value);

        if (accessory) {
            if (!this.checkConstructionAccessory(condition.value, conf)) {
                let color = null;
                if (Common.isArray(accessory.colors_ids) && accessory.price_source === 'colors') {
                    const windowColorId = this.colorMappingService.getWindowColorId(conf, 'inner');
                    const matchedColors = this.colorMappingService.getColors(windowColorId, 'window', 'accessory');
                    const matchedColor = matchedColors.find(el => accessory.colors_ids.some(id => Number(id) === el));
                    if (matchedColor) {
                        color = this.accessoriesService.colorsAll
                            .find(el => matchedColor === Number(el.id));
                    } else {
                        color = this.accessoriesService.colorsAll
                        .find(el => accessory.colors_ids.some(id => id == el.id));
                    }
                    accessory.selectedColor = color.id;
                } else if (accessory.price_source === 'table') {
                    const colorInner = this.colorsDefaultsService.getColorValue(conf.Colors, 'inner').toLowerCase();
                    const colorOuter = this.colorsDefaultsService.getColorValue(conf.Colors, 'outer').toLowerCase();
                    let colorOptions = 'white';
                    if ((colorInner === 'white' || colorInner === 'none') && colorOuter === 'color') {
                        colorOptions = 'white-color';
                    } else if ((colorOuter === 'white' || colorOuter === 'none') && colorInner === 'color') {
                        colorOptions = 'color-white';
                    } else if (colorOuter === 'color' && colorInner === 'color') {
                        colorOptions = 'color-color';
                    }
                    accessory.colorOptions = colorOptions;
                }
                accessory.fromDependency = dependencyId;
                accessory.addedWithDependency = true;
                accessory.dependencyAssociationIds = dependencyAssociationIds;

                if (condition.type === 'addRemovableAccessoryToConstruction') {
                    accessory.addedWithRemovableDependency = true;
                }

                this.accessoriesService.add('construction', accessory, color);
            } else {
                this.accessoriesService.markAccessoryWithDependencyId(condition, conf, dependencyId);
                this.accessoriesService.markAccessoryWithDependencyAssociationIds(condition, conf, dependencyAssociationIds);
            }
        } else {
            const foundDependency = this.occurrence.find(k => k.id === dependencyId.split('-')[0] && (k.kind === 'removableAccessory' || !k.kind));
            if (foundDependency && !this.checkConstructionAccessory(condition.value, conf)) {
                foundDependency.occurrence = 0;
            }
        }

        return true;
    }

    /**
     * Dodanie dodatku do skrzydła
     * @param {string} value Wartość do sprawdzenia
     * @param {object} sash  Konfiguracja skrzydła
     * @returns {boolean}
     * @memberof DependenciesService
     */
    addAccessoryToSash(value, sash, conf, sashDependenciesAccessories = []) {
        if (
            sash != null
            && Common.isArray(sash.hardware)
            && !sash.hardware.some(el => el.accesory.id == value && el.accesory.fromDependency)
        ) {
            let accessory = this.accessoriesService.getAccessory(value);
            if (accessory) {
                const dependencyAccessory = sashDependenciesAccessories.find(el => el.accessory.id == accessory.id);
                let color = null;
                if (dependencyAccessory) {
                    accessory = dependencyAccessory.accessory;
                    color = color = this.accessoriesService.colorsAll
                        .find(el => el.id == accessory.selectedColor);
                } else {
                    this.accessoriesService.setAmountAccessory(accessory, 'sash', conf, sash);
                    if (Common.isArray(accessory.colors_ids) && accessory.price_source === 'colors') {
                        const windowColorId = this.colorMappingService.getWindowColorId(conf, 'inner');
                        const matchedColors = this.colorMappingService.getColors(windowColorId, 'window', 'accessory');
                        const matchedColor = matchedColors.find(el => accessory.colors_ids.some(id => Number(id) === el));
                        if (matchedColor) {
                            color = this.accessoriesService.colorsAll
                                .find(el => matchedColor === Number(el.id));
                        } else {
                            color = this.accessoriesService.colorsAll
                            .find(el => accessory.colors_ids.some(id => id == el.id));
                        }
                        accessory.selectedColor = color.id;
                    } else if (accessory.price_source === 'table') {
                        const colorInner = this.colorsDefaultsService.getColorValue(conf.Colors, 'inner').toLowerCase();
                        const colorOuter = this.colorsDefaultsService.getColorValue(conf.Colors, 'outer').toLowerCase();
                        let colorOptions = 'white';
                        if ((colorInner === 'white' || colorInner === 'none') && colorOuter === 'color') {
                            colorOptions = 'white-color';
                        } else if ((colorOuter === 'white' || colorOuter === 'none') && colorInner === 'color') {
                            colorOptions = 'color-white';
                        } else if (colorOuter === 'color' && colorInner === 'color') {
                            colorOptions = 'color-color';
                        }
                        accessory.colorOptions = colorOptions;
                    }
                    accessory.fromDependency = true;
                }
                this.accessoriesService.add(sash, accessory, color);
            }
        }
        return true;
    }

    /**
     * Sprawdzanie systemu produktu
     * @param {string} value Wartość do sprawdzenia
     * @param {object} conf  Konfiguracja pozycji
     * @returns {boolean}
     * @memberof DependenciesService
     */
    checkProfile(value, conf) {
        if (
            Common.isArray(conf.UsedProfiles)
            && value
            && conf.UsedProfiles.some(profile => (
                profile.type === value.type
                && (value.options || []).every(o => profile.options && profile.options.indexOf(o) > -1)
                && (value.notOptions || []).every(o => profile.options && profile.options.indexOf(o) === -1)
            ))
        ) {
            return true;
        }
        return false;
    }

    /**
     * Sprawdzanie skracania drzwi
     * @param {string} value Wartość do sprawdzenia
     * @param {object} conf  Konfiguracja pozycji
     * @returns {boolean}
     * @memberof DependenciesService
     */
    checkShortening(value, conf) {
        return conf.doorSizes
            && conf.doorSizes.shortening
            && value.from <= conf.doorSizes.shortening
            && value.to >= conf.doorSizes.shortening;
    }

    /**
     * Sprawdzanie profilu ramy
     * @param {string} value Wartość do sprawdzenia
     * @param {object} conf  Konfiguracja pozycji
     * @returns {boolean}
     * @memberof DependenciesService
     */
    checkFrameProfile(value, conf) {
        if(DoorActiveConfiguration.is(conf) && conf.System.door_type) {
            if (Common.isObject(conf.ProfileSet) && Common.isDefined(conf.ProfileSet.frameSide) && value && value.map(Number).indexOf(Number(conf.ProfileSet.frameSide)) > -1) {
                return true;
            }
        } else  {
            if (Common.isObject(conf.UsedProfiles) && conf.UsedProfiles.filter(profile => profile.type === 'frame').length > 0 && value && conf.UsedProfiles.filter(profile => profile.type === 'frame').some(profile => value.map(Number).includes(profile.id))) {
               return true;
            }
        }
        return false;
    }

    /**
     * Sprawdzanie profilu progu
     * @param {string} value Wartość do sprawdzenia
     * @param {object} conf  Konfiguracja pozycji
     * @returns {boolean}
     * @memberof DependenciesService
     */
    checkThresholdProfile(value, conf: WindowActiveConfiguration) {
        if(DoorActiveConfiguration.is(conf) && conf.System.door_type) {
            if (Common.isObject(conf.ProfileSet) &&
                Common.isDefined(conf.ProfileSet.threshold) &&
                value &&
                value.map(Number).indexOf(Number(conf.ProfileSet.threshold)) > -1 &&
                conf.Frames.some(frame => frame.lowThreshold)
            ) {
                return true;
            }
        } else {
            if (Common.isObject(conf.UsedProfiles) && conf.UsedProfiles.filter(profile => profile.type === 'threshold').length > 0 && value && conf.UsedProfiles.filter(profile => profile.type === 'threshold').some(profile => value.map(Number).includes(profile.id))) {
                return true;
            }
        }
        return false;
    }

    /**
     * Sprawdzanie skracania ościeżnicy
     * @param {string} value Wartość do sprawdzenia
     * @param {object} conf  Konfiguracja pozycji
     * @returns {boolean}
     * @memberof DependenciesService
     */
     checkFrameShortening(value, conf) {
        const frameShortening = conf.doorSizes && conf.doorSizes.frameShortening ? conf.doorSizes.frameShortening : 0;
        return value.from <= frameShortening && value.to >= frameShortening;
    }
    /**
     * Sprawdzanie niskiego progu
     * @param {string} value Wartość do sprawdzenia
     * @param {object} conf  Konfiguracja pozycji
     * @returns {boolean}
     * @memberof DependenciesService
     */
    checkLowThreshold(value, conf) {
        if (!conf || !Array.isArray(conf.Frames)) {
            return false;
        }
        if (value === true || value === 'true') {
            const result = conf.Frames.some(frame => frame.lowThreshold === true);
            return result;
        }
        if (value === false || value === 'false') {
            const result = conf.Frames.every(frame => !frame.lowThreshold);
            return result;
        }
        return false;
    }


    private removeDependentAccessories(conf, dependencyId) {
        if (conf.Accessories && dependencyId) {
            const accessoriesFromDependency = conf.Accessories.filter(
                (el) =>
                    el.accessory?.fromDependency &&
                    String(el.accessory?.fromDependency) === String(dependencyId)
            );

            accessoriesFromDependency.forEach((accessory) => {
                accessory && this.accessoriesService.remove(null, accessory, true);
            });
        }
    }

    getDependencyById(id: number) {
        return this.configuratorsDataService.data.dependencies.find(d => d.id == id);
    }

    private removeDependentItems(value, conf, dependencyId) {
        const accessoriesFromThatDependency =
            Common.isArray(conf.Accessories) &&
            conf.Accessories.filter(
                (el) =>
                    el.accessory &&
                    el.accessory.fromDependency != null &&
                    el.accessory.fromDependency !== false &&
                    String(el.accessory.fromDependency) === String(dependencyId) &&
                    Array.isArray(el.accessory.dependencyAssociationIds) &&
                    this.compareDependencyAssociationIds(value, el.accessory.dependencyAssociationIds)
            );
        let removeFomDependency = true;
        accessoriesFromThatDependency.forEach((accessory) => {
            this.accessoriesService.markAccessoryWithDependencyId(
                accessory.accessory.id,
                conf,
                false
            );
            if (accessory.accessory.addedWithDependency) {
                const foundDependency = this.occurrence.find(k => k.id === dependencyId && (k.kind === 'removableAccessory' || !k.kind));
                if(!accessory.accessory.addedWithRemovableDependency || foundDependency) {
                    this.accessoriesService.remove(null, accessory, true);
                    if(foundDependency) {
                        foundDependency.occurrence = 0;
                    }
                } else {
                    removeFomDependency = false
                }
                if(accessory.accessory.addedWithRemovableDependency && !foundDependency) {
                    this.occurrence.push({ id: dependencyId, occurrence: 1 });
                }
            }
        });
        if(removeFomDependency) {
            conf.Accessories.filter(
                (el) =>
                    el.accessory &&
                    el.accessory.fromDependency != null &&
                    el.accessory.fromDependency !== false &&
                    String(el.accessory.fromDependency) === String(dependencyId)
                    && !Array.isArray(el.accessory.dependencyAssociationIds)
            ).forEach(accessory => {
               accessory.accessory.fromDependency = null;
            });
        }

    }

    private validateFrames(conf) {
        this.profilesService.validateFramesAndFixIssues(conf);
    }

    private compareDependencyAssociationIds(value, accessoryDependencyAssociationIds) {
        if (Array.isArray(value)) {
            return accessoryDependencyAssociationIds.some(dep => {
                if (Array.isArray(dep) && dep.map(Number).every((dependencyAssociationId) => value.map(Number).includes(dependencyAssociationId))) {
                    return true;
                } else {
                    return value.map(Number).includes(Number(dep));
                }
            });
        } else {
            return accessoryDependencyAssociationIds.some(dep => {
                if (value == null && dep == null) return true;
                if (Array.isArray(dep)) {
                    return dep.map(Number).includes(Number(value));
                } else {
                    return Number(dep) === Number(value);
                }
            });
        }
    }

    getPulls(conf) {
        return (this.configuratorsDataService.data.pullType || []).filter((item) => {
            if (item?.window_lines_ids.indexOf(conf.System?.id) > -1) {
                return item;
            }
        });
    }

    getRecessedPulls(conf) {
        return (this.configuratorsDataService.data.recessedPulls || []).filter(item => item?.window_lines_ids.indexOf(conf.System?.id) > -1);
    }

    isPullBlockadeAvailable(conf, dependency, condition): boolean {
        const blockade = dependency.actions.find((p) => p.value === 'blockade_to_configuration');
        const isPullAvailable = this.getPulls(conf).some(
            (p) => Number(p?.id) === Number(condition?.value)
        );
        const isRecessedPullAvailable = this.getRecessedPulls(conf).some(
            (p) => Number(p?.id) === Number(condition?.value)
        );

        return blockade && (isPullAvailable || isRecessedPullAvailable);
    }

    getBlockedAccessories(accessoryId, conf) {
        const accessoriesToBlock = [];

        accessoriesToBlock.push(...accessoryId);

        accessoriesToBlock.forEach((element) => {
            if (!conf.BlockedAccessories.includes(element)) {
                conf.BlockedAccessories.push(element);
            }
        })
    }

    checkSlat(conf) {
        const activeSash = conf.Sashes.find(s => s.type && (s.type.type === 'DRA' || s.type.type === 'DOA'));
        if(!activeSash || !activeSash.slat) {
            return false;
        }
        return activeSash.slat.count;
    }

    checkHasAlushell(conf) {
       return conf?.HasAlushell ?? false
    }

    checkAlushellColorGroup(value, conf, type: 'sash' | 'frame' = 'sash') {
        if(type === 'sash' && !conf.Sashes.some(x => x.type.handle_position)) {
            return false;
        }
        if (!conf?.HasAlushell || !value || !value || !Array.isArray(value) || !conf.Colors?.[type] || !conf.Colors?.[type]?.alushell?.colorGroups) {
            return false
        }
        const colorGroups = conf.Colors[type].alushell.colorGroups;
        const conditionColorGroups = value.map(Number);
        return colorGroups.some(colorGroup => conditionColorGroups.includes(colorGroup));

    }

    /**
     * Tworzy hash konfiguracji na podstawie kluczowych pól które mogą się zmienić podczas przetwarzania zależności
     * @param {object} conf Konfiguracja
     * @returns {string} Hash konfiguracji
     * @memberof DependenciesService
     *
     */
    private createConfigSnapshot(conf: any): string {
        const hashParts: string[] = [];

        if (conf.Sashes) {
            conf.Sashes.forEach((sash, idx) => {
                const hardwareIds = sash.hardware?.map(h => h.accessory?.id).filter(Boolean).sort().join(',') || '';
                const handleId = sash.handle?.id || '';
                hashParts.push(`s${idx}:${hardwareIds}:${handleId}`);
            });
        }

        const accessoriesIds = conf.Accessories?.map(a => `${a.accessory?.id}-${a.accessory?.fromDependency || ''}`).sort().join(',') || '';
        hashParts.push(`acc:${accessoriesIds}`);

        if (conf.SideAccessories) {
            ['top', 'bottom', 'left', 'right'].forEach(side => {
                const sideAccIds = conf.SideAccessories[side]?.map(a => a.accessory?.id).filter(Boolean).sort().join(',') || '';
                hashParts.push(`sa-${side}:${sideAccIds}`);
            });
        }

        if (conf.doorHardware) {
            hashParts.push(`dh:${conf.doorHardware.pull || ''}:${conf.doorHardware.recessedPull || ''}:${conf.doorHardware.doorViewer || ''}:${conf.doorHardware.plates?.mainLock || ''}:${conf.doorHardware.plates?.additionalLock || ''}:${conf.doorHardware.outerPlates?.mainLock || ''}:${conf.doorHardware.outerPlates?.additionalLock || ''}:${conf.doorHardware.cylinders?.mainLock || ''}:${conf.doorHardware.cylinders?.additionalLock || ''}`);
        }


        hashParts.push(`lock:${conf.Lock?.id || ''}`);
        hashParts.push(`handle:${conf.Handle?.id || ''}`);
        hashParts.push(`hinge:${conf.Hinge?.id || ''}`);
        hashParts.push(`handleOuter:${conf.HandleOuter?.id || ''}`);

        const sideProfilesHash = conf.SideProfiles?.map(sp => `${sp.side}:${sp.profileId}`).sort().join(',') || '';
        hashParts.push(`sp:${sideProfilesHash}`);


        if (conf.ProfileSet) {
            hashParts.push(`ps:${conf.ProfileSet.frameSide || ''}:${conf.ProfileSet.threshold || ''}`);
        }

        if (conf.Colors) {
            hashParts.push(`col:${conf.Colors.frame?.inner?.id || ''}:${conf.Colors.frame?.outer?.id || ''}`);
        }


        const blockedAccIds = conf.BlockedAccessories?.slice().sort().join(',') || '';
        hashParts.push(`blocked:${blockedAccIds}`);

        return hashParts.join('|');
    }

    /**
     * Porównuje dwa hashe konfiguracji
     * @param {string} snapshot1 Pierwszy hash
     * @param {string} snapshot2 Drugi hash
     * @returns {boolean} true jeśli hashe są identyczne
     * @memberof DependenciesService
     */
    private areConfigSnapshotsEqual(snapshot1: string, snapshot2: string): boolean {
        return snapshot1 === snapshot2;
    }
}
